import {
  IFormMaterial,
  IInspectionOptions,
  ISignature,
  ISiteLocation,
  PermitFieldType,
} from '~/client/graph/types.generated'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MaterialsStore from '~/client/src/shared/stores/domain/Materials.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import { IPermitFormField } from '~/client/src/shared/stores/ui/PermitFieldsStores/PermitFieldsBase.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { DASH } from '~/client/src/shared/utils/usefulStrings'
import { areArraysEqualUnordered } from '~/client/src/shared/utils/util'

import {
  additionalDateMapper,
  checklistMapper,
  companyMapper,
  fileMapper,
  locationMapper,
  materialMapper,
  measureMapper,
  richTextMapper,
  signatureMapper,
  stringMapper,
  userMapper,
} from './changedFieldsMessageMappers'

const COMMA_SEPARATOR = ', '

export default class ChangedFieldsMessageFormatter {
  public constructor(
    private readonly projectMembersStore: ProjectMembersStore,
    private readonly companiesStore: CompaniesStore,
    private readonly locationAttributesStore: LocationAttributesStore,
    private readonly materialsStore: MaterialsStore,
    private readonly projectDateStore: ProjectDateStore,
    // Also names a location the form just created, before the legacy stores receive it.
    private readonly getLocationName: (
      location: ISiteLocation,
    ) => string | undefined,
  ) {}

  public toDisplayString = (value: any, field: IPermitFormField): string => {
    if (value === null || value === undefined) {
      return DASH
    }

    if (!Array.isArray(value)) {
      return this.fieldValueByTypeMap[field.type]?.(value, field) || DASH
    }

    if (!value.length) {
      return DASH
    }

    return value
      .map(item => this.toDisplayString(item, field))
      .join(COMMA_SEPARATOR)
  }

  private get fieldValueByTypeMap(): Record<
    PermitFieldType,
    (value: any, field?: IPermitFormField) => string
  > {
    return {
      [PermitFieldType.File]: fileMapper,
      [PermitFieldType.Assignee]: (userId: string) =>
        userMapper(userId, this.projectMembersStore),
      [PermitFieldType.Requester]: (userId: string) =>
        userMapper(userId, this.projectMembersStore),
      [PermitFieldType.Subscriber]: (userId: string) =>
        userMapper(userId, this.projectMembersStore),
      [PermitFieldType.User]: (userId: string) =>
        userMapper(userId, this.projectMembersStore),
      [PermitFieldType.Location]: this.getLocationName,
      [PermitFieldType.Equipment]: (location: ISiteLocation) =>
        locationMapper(location, this.locationAttributesStore),
      [PermitFieldType.LocationInformational]: this.getLocationName,
      [PermitFieldType.EquipmentInformational]: (location: ISiteLocation) =>
        locationMapper(location, this.locationAttributesStore),
      [PermitFieldType.Measure]: measureMapper,
      [PermitFieldType.Company]: (companyId: string) =>
        companyMapper(companyId, this.companiesStore),
      [PermitFieldType.CompanyInformational]: (companyId: string) =>
        companyMapper(companyId, this.companiesStore),
      [PermitFieldType.Material]: (material: IFormMaterial) =>
        materialMapper(material, this.materialsStore),
      [PermitFieldType.MaterialInformational]: (material: IFormMaterial) =>
        materialMapper(material, this.materialsStore),
      [PermitFieldType.MaterialResource]: null,
      [PermitFieldType.MaterialResourceInformational]: null,
      [PermitFieldType.Checklist]: checklistMapper,
      [PermitFieldType.Question]: checklistMapper,
      [PermitFieldType.RichText]: richTextMapper,
      [PermitFieldType.Email]: stringMapper,
      [PermitFieldType.InputNumber]: stringMapper,
      [PermitFieldType.InputText]: stringMapper,
      [PermitFieldType.InputTextArea]: stringMapper,
      [PermitFieldType.LinkedWorkflow]: stringMapper,
      [PermitFieldType.Activity]: stringMapper,
      [PermitFieldType.PrimaryActivity]: stringMapper,
      [PermitFieldType.PhoneNumber]: stringMapper,
      [PermitFieldType.Select]: stringMapper,
      [PermitFieldType.AdditionalDate]: (date: number) =>
        additionalDateMapper(date, this.projectDateStore),
      [PermitFieldType.Time]: stringMapper,
      [PermitFieldType.Signature]: (signature: ISignature) =>
        signatureMapper(signature, this.projectMembersStore),
      // not supported
      [PermitFieldType.Divider]: null,
      [PermitFieldType.Text]: null,
      [PermitFieldType.Instructions]: null,
      [PermitFieldType.StartFinishDates]: null,
      [PermitFieldType.Table]: null,
      [PermitFieldType.Section]: null,
      [PermitFieldType.Subsection]: null,
      [PermitFieldType.InlineMedia]: null,
    }
  }

  public getInspectionOptionsMessage = (
    oldOptions: IInspectionOptions,
    newOptions: IInspectionOptions,
    field: IPermitFormField,
  ): string => {
    if (!oldOptions && !newOptions) return null

    const changes: string[] = []

    if (oldOptions.deadlineTime !== newOptions.deadlineTime) {
      changes.push(
        `${oldOptions.deadlineTime || DASH} -> ${
          newOptions.deadlineTime || DASH
        }`,
      )
    }

    if (
      oldOptions.inspectionFrequency !== newOptions.inspectionFrequency ||
      oldOptions.inspectionFrequencyType !== newOptions.inspectionFrequencyType
    ) {
      changes.push(
        `${oldOptions.inspectionFrequency || DASH} ${
          oldOptions.inspectionFrequencyType || DASH
        } -> ${newOptions.inspectionFrequency || DASH} ${
          newOptions.inspectionFrequencyType || DASH
        }`,
      )
    }

    const oldDays = oldOptions.selectedDaysToRepeat || []
    const newDays = newOptions.selectedDaysToRepeat || []
    if (!areArraysEqualUnordered(oldDays, newDays)) {
      const weekdays = this.projectDateStore.weekdayNamesList
      const oldDaysNames = weekdays.filter((_, index) =>
        oldDays.includes(index + 1),
      )
      const newDaysNames = weekdays.filter((_, index) =>
        newDays.includes(index + 1),
      )
      changes.push(
        `${oldDaysNames.join(COMMA_SEPARATOR)} -> ${newDaysNames.join(
          COMMA_SEPARATOR,
        )}`,
      )
    }

    if (changes.length === 0) return null

    return `${field.caption}: ${changes.join(COMMA_SEPARATOR)}`
  }
}
